<script lang="ts">
  const LAST_UPDATED    = 'January 15, 2026';
  const POLICY_VERSION  = '2.1';
  const COMPANY_ADDRESS = 'bleunk.com';
</script>

<svelte:head>
  <title>Privacy Policy — Global Data Protection — Bleunk</title>
  <meta name="description" content="Bleunk Privacy Policy — Global data protection standards and AI audit logs. Learn how we secure and protect your personal data." />
  <meta name="keywords" content="Bleunk privacy policy, data privacy, data protection, AI data protection, personal data rights, global data compliance, Bleunk Studio" />

  <!-- Open Graph -->
  <meta property="og:title" content="Privacy Policy — Data Protection — Bleunk" />
  <meta property="og:description" content="How Bleunk Studio collects, uses, and protects your personal data with global standards and AI transparency." />
  <meta property="og:url" content="https://bleunk.com/privacy" />
  <meta property="og:image" content="https://bleunk.com/og-default.png" />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="Bleunk" />

  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://bleunk.com/privacy" />

  {@html `<script type="application/ld+json">${JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://bleunk.com/privacy#webpage",
    "url": "https://bleunk.com/privacy",
    "name": "Privacy Policy — Bleunk Studio",
    "description": "Bleunk Studio Privacy Policy. Designed for alignment with the Philippine Data Privacy Act (RA 10173).",
    "dateModified": "2026-01-15",
    "datePublished": "2024-01-01",
    "isPartOf": { "@id": "https://bleunk.com/#website" },
    "about": { "@id": "https://bleunk.com/#organization" },
    "inLanguage": "en-PH",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://bleunk.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Privacy Policy",
          "item": "https://bleunk.com/privacy"
        }
      ]
    }
  })}</script>`}
</svelte:head>

<section class="relative pt-32 pb-24 px-4">
  <div class="max-w-3xl mx-auto">

    <!-- Header -->
    <div class="mb-12">
      <div class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dim-8 bg-dim-3 text-dim-50 text-xs font-medium mb-4">
        Privacy Policy
      </div>
      <h1 class="font-display text-4xl sm:text-5xl font-bold mb-4">
        Your data.<br />
        <span class="gradient-accent-text">Your rights.</span>
      </h1>
      <div class="flex flex-wrap gap-x-4 gap-y-1 text-dim-40 text-sm">
        <span>Version {POLICY_VERSION}</span>
        <span>·</span>
        <span>Last updated {LAST_UPDATED}</span>
        <span>·</span>
        <span>Effective immediately</span>
      </div>
    </div>

    <!-- Summary banner -->
    <div class="rounded-2xl border border-[#C9A87C]/20 bg-[#C9A87C]/5 p-5 mb-10">
      <p class="text-sm text-dim-70 leading-relaxed">
        <strong class="text-content">Summary for humans:</strong> We collect only what's necessary
        to deliver the service. We never sell your data. You can export or delete it at any time.
        Our AI agents are engineered for auditability and always subject to human override.
        This policy is designed for alignment with the Philippine Data Privacy Act (RA 10173).
      </p>
    </div>

    <div class="space-y-6">

      <!-- 1. Who We Are -->
      <div class="rounded-2xl border border-dim-6 bg-card p-8">
        <h2 class="font-display text-2xl font-bold mb-5 flex items-center gap-2">
          <span class="text-[#C9A87C]">§</span> 1. Who We Are
        </h2>
        <div class="space-y-4 text-sm text-dim-60 leading-relaxed">
          <p>
            <strong class="text-content">Personal Information Controller (PIC):</strong><br />
            Bleunk Studio
          </p>
          <p>
            <strong class="text-content">Data Protection Officer (DPO):</strong><br />
            Email: <a href="mailto:hello@bleunk.com" class="text-[#C9A87C] hover:underline underline-offset-2">hello@bleunk.com</a><br />
            For all privacy-related inquiries and data subject requests.
          </p>
        </div>
      </div>

      <!-- 2. Data We Collect -->
      <div class="rounded-2xl border border-dim-6 bg-card p-8">
        <h2 class="font-display text-2xl font-bold mb-5 flex items-center gap-2">
          <span class="text-[#C9A87C]">§</span> 2. Data We Collect
        </h2>
        <div class="space-y-3">
          {#each [
            { category: 'Account Data',         basis: 'Contract Performance',        data: 'Name, email address, hashed password, plan type, consent timestamp.',                                                                        retention: '5 years post-account closure'              },
            { category: 'Usage & Analytics',    basis: 'Legitimate Interest / Consent', data: 'Pages visited, features used, browser type, anonymized IP address, session duration.',                                                    retention: '13 months'                                 },
            { category: 'Payment Data',         basis: 'Contract Performance',        data: 'Transaction reference IDs, payment method type (handled by secure providers). Billing name and address.',                                    retention: '7 years (BIR requirement)'                 },
            { category: 'AI Interaction Logs',  basis: 'Legal Obligation / Contract', data: 'Prompts submitted, generated outputs, agent interaction transcripts, escalation records, human override actions.',                           retention: '3 years' },
            { category: 'Cookie & Consent Data', basis: 'Consent',                   data: 'Consent timestamp, selected consent categories, anonymized session token.',                                                                    retention: '2 years'                                   },
          ] as item}
            <div class="p-4 bg-dim-4 rounded-xl border border-dim-6">
              <div class="flex items-center justify-between mb-2">
                <div class="font-medium text-content text-sm">{item.category}</div>
                <span class="inline-flex px-2 py-0.5 rounded-full bg-[#C9A87C]/10 border border-[#C9A87C]/20 text-[#C9A87C] text-xs font-medium">{item.basis}</span>
              </div>
              <div class="text-dim-60 text-xs leading-relaxed mb-2">{item.data}</div>
              <div class="text-dim-40 text-xs">Retention: {item.retention}</div>
            </div>
          {/each}
        </div>
        <p class="text-dim-40 text-xs mt-4">
          Card numbers and full payment credentials are never stored by Bleunk. All payment
          processing is handled by secure, regulated payment gateways.
        </p>
      </div>

      <!-- 3. How We Use Data -->
      <div class="rounded-2xl border border-dim-6 bg-card p-8">
        <h2 class="font-display text-2xl font-bold mb-5 flex items-center gap-2">
          <span class="text-[#C9A87C]">§</span> 3. How We Use Your Data
        </h2>
        <ul class="space-y-2 text-sm text-dim-60">
          {#each [
            'Deliver and operate the Bleunk platform and all subscribed services',
            'Process payments and manage subscription billing',
            'Deliver custom automation solutions and web development projects',
            'Maintain audit logs of AI agent actions for quality and accountability',
            'Send transactional emails (receipts, service notices, security alerts)',
            'Provide customer support and resolve disputes',
            'Improve platform accuracy and agent performance using anonymized aggregate data',
            'Comply with relevant legal obligations',
          ] as item}
            <li class="flex items-start gap-2.5">
              <svg class="w-4 h-4 text-[#7A9E90] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              {item}
            </li>
          {/each}
        </ul>
        <p class="text-dim-60 text-sm mt-5 pt-5 border-t border-dim-5">
          We do <strong class="text-content">not</strong> sell, rent, or trade personal data to third
          parties for marketing purposes.
        </p>
      </div>

      <!-- 4. AI Systems -->
      <div class="rounded-2xl border border-[#8E9BAF]/20 bg-card p-8">
        <h2 class="font-display text-2xl font-bold mb-5 flex items-center gap-2">
          <span class="text-[#C9A87C]">§</span> 4. AI Systems & Automated Processing
        </h2>
        <p class="text-sm text-dim-60 leading-relaxed mb-5">
          Bleunk deploys AI agents that process data during interactions. To ensure accountability, we maintain:
        </p>
        <div class="space-y-3">
          {#each [
            { title: 'Audit Logs',             color: 'steel', body: 'AI agent actions are timestamped and stored for review. Logs include: input received, decision/output generated, and human override actions.' },
            { title: 'Human-in-the-Loop',     color: 'sage',  body: 'Agents are configured with escalation triggers for complex or sensitive requests, routing them to human operators.' },
            { title: 'Operational Guardrails',  color: 'gold',  body: 'AI agents cannot execute high-value financial transactions without explicit human approval.' },
            { title: 'Transparency',    color: 'gold',  body: "We inform users when they are communicating with an AI system and disclose the agent's capability boundaries." },
          ] as item}
            {@const styles = {
              gold:  'bg-[#C9A87C]/10 border-[#C9A87C]/20 text-[#C9A87C]',
              steel: 'bg-[#8E9BAF]/10 border-[#8E9BAF]/20 text-[#8E9BAF]',
              sage:  'bg-[#7A9E90]/10 border-[#7A9E90]/20 text-[#7A9E90]',
            }[item.color]}
            <div class="p-4 bg-dim-4 rounded-xl border border-dim-6">
              <span class="inline-flex px-2 py-0.5 rounded-full border text-xs font-medium mb-2 {styles}">{item.title}</span>
              <p class="text-dim-60 text-xs leading-relaxed">{item.body}</p>
            </div>
          {/each}
        </div>
      </div>

      <!-- 5. Data Subject Rights -->
      <div class="rounded-2xl border border-dim-6 bg-card p-8">
        <h2 class="font-display text-2xl font-bold mb-5 flex items-center gap-2">
          <span class="text-[#C9A87C]">§</span> 5. Your Rights Under RA 10173
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {#each [
            { right: 'Right to be Informed',     desc: 'Know what data we collect, why, and how it is used.' },
            { right: 'Right to Access',          desc: 'Request a copy of all personal data we hold about you.' },
            { right: 'Right to Rectification',   desc: 'Correct inaccurate or incomplete personal data.' },
            { right: 'Right to Erasure',         desc: 'Request deletion of your personal data, subject to retention obligations.' },
            { right: 'Right to Object',          desc: 'Object to the processing of your personal data for specific purposes.' },
            { right: 'Right to Data Portability', desc: 'Receive your data in a structured, machine-readable format.' },
            { right: 'Right to Lodge a Complaint', desc: 'File a complaint with the National Privacy Commission.' },
            { right: 'Right to Damages',         desc: 'Claim compensation for damages suffered as a result of a valid violation.' },
          ] as item}
            <div class="p-4 bg-dim-4 rounded-xl border border-dim-6">
              <div class="font-medium text-content text-sm mb-1">{item.right}</div>
              <div class="text-dim-50 text-xs leading-relaxed">{item.desc}</div>
            </div>
          {/each}
        </div>
        <p class="text-dim-50 text-sm mt-5">
          To exercise any of these rights, email
          <a href="mailto:hello@bleunk.com" class="text-[#C9A87C] hover:underline underline-offset-2">hello@bleunk.com</a>.
        </p>
      </div>

      <!-- 6. Cookies -->
      <div class="rounded-2xl border border-dim-6 bg-card p-8">
        <h2 class="font-display text-2xl font-bold mb-5 flex items-center gap-2">
          <span class="text-[#C9A87C]">§</span> 6. Cookies & Tracking
        </h2>
        <div class="space-y-4 text-sm text-dim-60 leading-relaxed">
          <p>We use cookies for three purposes, each requiring separate consent:</p>
          <div class="space-y-2">
            {#each [
              { name: 'Functional (Required)', desc: 'Session management and security. Cannot be disabled.', required: true  },
              { name: 'Analytics (Optional)',  desc: 'Anonymized usage statistics to improve platform performance.', required: false },
              { name: 'Marketing (Optional)', desc: 'Personalized content. Only enabled with explicit consent.', required: false },
            ] as cookie}
              <div class="p-3 bg-dim-4 rounded-xl border border-dim-6 flex items-start gap-3">
                <span class="inline-flex px-2 py-0.5 rounded-full border text-xs font-medium shrink-0 {cookie.required
                  ? 'bg-[#7A9E90]/10 border-[#7A9E90]/20 text-[#7A9E90]'
                  : 'bg-dim-5 border-dim-8 text-dim-50'}">
                  {cookie.required ? 'Required' : 'Optional'}
                </span>
                <div>
                  <div class="font-medium text-content text-xs mb-0.5">{cookie.name}</div>
                  <div class="text-dim-40 text-xs">{cookie.desc}</div>
                </div>
              </div>
            {/each}
          </div>
          <p>Consent preferences can be updated at any time via the cookie banner.</p>
        </div>
      </div>

      <!-- 7. Security -->
      <div class="rounded-2xl border border-dim-6 bg-card p-8">
        <h2 class="font-display text-2xl font-bold mb-5 flex items-center gap-2">
          <span class="text-[#C9A87C]">§</span> 7. Data Security
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs">
          {#each [
            'AES-256 encryption at rest',
            'TLS 1.3 in transit',
            'Industry-standard hashing',
            'Strict-Transport-Security headers',
            'Cloud-native security architecture',
            'Regular vulnerability scanning',
            'Breach notification per RA 10173',
            'Privacy impact assessments',
          ] as measure}
            <div class="flex items-center gap-2.5 p-3 bg-dim-4 rounded-xl border border-dim-6">
              <svg class="w-3.5 h-3.5 text-[#7A9E90] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span class="text-dim-60">{measure}</span>
            </div>
          {/each}
        </div>
      </div>

      <!-- 8. Changes -->
      <div class="rounded-2xl border border-dim-6 bg-card p-8">
        <h2 class="font-display text-2xl font-bold mb-5 flex items-center gap-2">
          <span class="text-[#C9A87C]">§</span> 8. Changes to This Policy
        </h2>
        <p class="text-sm text-dim-60 leading-relaxed">
          We reserve the right to update this Privacy Policy to reflect changes in our practices or
          legal requirements. Material changes will be communicated via our website. Continued use of the service constitutes acceptance of the
          updated policy.
        </p>
      </div>

    </div>

    <div class="mt-12 text-center text-dim-30 text-xs">
      Bleunk Studio · hello@bleunk.com<br />
      Effective {LAST_UPDATED}.
    </div>

  </div>
</section>
